import { Row, Spin } from 'antd';
import React, { FC, Suspense, lazy } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import i18n from 'src/locales/i18n';
import { PAGE_ROUTE, PAGE_ROUTE_WITH_PATH } from './constants/route';
import { RootStoreProvider } from './context';
import ForgetPasswordEmail from './pages/login/components/forgot-password-email/forgot-password-email';
import ForgetPasswordPage from './pages/login/components/forgot-password/forgot-password.page';
import NewPasswordPage from './pages/login/components/new-password/new-password.page';
import CompanyDetailPage from './pages/protected/funder/coporate/company-detail';
import ViewMortgageDealForm from './pages/protected/mortgage/mortgage-application-list/view-mortgage-deal-form/view-mortgage-deal-form';
import MortgageDealForm from './pages/protected/mortgage/mortgage-deal-form/mortgage-deal-form';

import IndividualAccountPage from './pages/protected/onboarding/individual-account-detail/individual-account-detail';
import rootStore from './store';

import MainLayout from './layouts/main/main.layout';
const MortgageDealPage = lazy(() => import('./pages/protected/mortgage-deal/mortgage-deal.page'));
const MortgageDealDetailPage = lazy(() => import('./pages/protected/mortgage-deal/mortgage-deal-detail/mortgage-deal-detail'));


const LoginPage = lazy(() => import('./pages/login/login.page'));
const SelectAccountPage = lazy(() => import('./pages/registration/select-account-type/select-account.page'));
const RegistrationPage = lazy(() => import('./pages/registration/registration.page'));
const OtpPage = lazy(() => import('./pages/protected/otp/otp.page'));
const ProfilePage = lazy(() => import('./pages/protected/profile.page'));
const ContactUs = lazy(() => import('./pages/contact-us/contact-us.page'));
const NotFound = lazy(() => import('./pages/404/404.page'));
const ComingSoon = lazy(() => import('./pages/coming-soon/coming-soon.page'));
const IndividualBorrowerRequestPage = lazy(
	() => import('./pages/protected/borrower/individual/individual-request-form')
);
const IndividualFunderRequestPage = lazy(
	() => import('./pages/protected/funder/request-form/individual/individual-request-form')
);
const CorporateBorrowerRequestPage = lazy(() => import('./pages/protected/borrower/corporate/corporate-request-form'));
const CorporateFunderRequestPage = lazy(
	() => import('./pages/protected/funder/request-form/corporate/corporate-request-form')
);
const GuarantorInvitationPage = lazy(() => import('./pages/protected/guarantor-invitation/guarantor-invitation'));
const GuarantorViewInvitationPage = lazy(() => import('./pages/protected/guarantee/guarantee-info/guarantor-view'));

const GuarantorSubmitInvitationPage = lazy(() => import('./pages/protected/guarantee/guarantee-info/guarantor-submit'));
const CorporateMortgageDealForm = lazy(
	() => import('./pages/protected/mortgage/mortgage-deal-form/corporate-deal-form')
);

const MortgageOfferAndPaymentStepPage = lazy(()=>import('./pages/protected/mortgage/offer-letter-step/offer-letter-step'));

const OfferLetterPreviewPage = lazy(()=> import('./pages/protected/mortgage/offer-letter-step/components/offer-letter/offer-letter'));

const PaymentResultPage = lazy(()=>import('./pages/protected/mortgage/offer-letter-step/payment-result/payment-result'));

const PropCapApp: FC = () => {
	return (
		<div className="App">
			<I18nextProvider i18n={i18n}>
				<RootStoreProvider store={rootStore}>
					<Suspense
						fallback={
							<Row justify="center" align="middle">
								<Spin />
							</Row>
						}>
						<BrowserRouter>
							<Routes>
								<Route path={'/'} element={<LoginPage />} caseSensitive />

								<Route path={PAGE_ROUTE.DASHBOARD + '/*'} element={<ProfilePage />} caseSensitive />

								<Route element={<MainLayout fullWidth removeMenu><Outlet /></MainLayout>} caseSensitive>
									<Route path={PAGE_ROUTE.MORTGAGE_DEALS} element={<MortgageDealPage />} />
									<Route path={PAGE_ROUTE.MORTGAGE_DEAL_DETAIL} element={<MortgageDealDetailPage />} />
								</Route>

								<Route path={PAGE_ROUTE.LOGIN} element={<LoginPage />} caseSensitive />
								<Route path={PAGE_ROUTE.ACCOUNT_TYPE} element={<SelectAccountPage />} caseSensitive />
								<Route path={PAGE_ROUTE.CONTRACT_US} element={<ContactUs />} caseSensitive />
								<Route path={PAGE_ROUTE_WITH_PATH.REGISTRATION} element={<RegistrationPage />} />
								<Route path={PAGE_ROUTE.OTP} element={<OtpPage />} />
								<Route path={PAGE_ROUTE.NOT_FOUND} element={<NotFound />} />
								<Route path={PAGE_ROUTE.COMMING_SOON} element={<ComingSoon/>} caseSensitive/>
								<Route path={PAGE_ROUTE.FORGOT_PASSWORD} element={<ForgetPasswordPage />} caseSensitive />
								<Route path={PAGE_ROUTE.FORGOT_PASSWORD_EMAIL} element={<ForgetPasswordEmail />} />
								<Route path={PAGE_ROUTE_WITH_PATH.CHANGE_FORGOTTEN_PASS} element={<NewPasswordPage />} />
								<Route path={PAGE_ROUTE.FUNDER_ACCOUNT} element={<IndividualAccountPage />} />
								<Route path={PAGE_ROUTE.FUNDER_REQUEST_FORM} element={<IndividualFunderRequestPage />} />
								<Route path={PAGE_ROUTE.FUNDER_COMPANY} element={<CompanyDetailPage />} />
								<Route path={PAGE_ROUTE.FUNDER_COMPANY_REQUEST_FORM} element={<CorporateFunderRequestPage />} />

								<Route path={PAGE_ROUTE.BORROWER_ACCOUNT} element={<IndividualAccountPage />} />
								<Route path={PAGE_ROUTE.BORROWER_ACCOUNT_FORM} element={<IndividualBorrowerRequestPage />} />
								<Route path={PAGE_ROUTE.BORROWER_COMPANY} element={<CompanyDetailPage />} />
								<Route path={PAGE_ROUTE.BORROWER_COMPANY_REQUEST} element={<CorporateBorrowerRequestPage />} />

								<Route path={PAGE_ROUTE.MORTGAGE_DEAL_FORM} element={<MortgageDealForm />} />
								<Route path={PAGE_ROUTE.MORTGAGE_DEAL_CORPORATE_FORM} element={<CorporateMortgageDealForm />} />

								<Route path={PAGE_ROUTE.DASHBOARD_GUARANTEE_INVITATION} element={<GuarantorInvitationPage />} />
								<Route path={PAGE_ROUTE.DASHBOARD_VIEW_GUARANTEE} element={<GuarantorViewInvitationPage />} />
								<Route path={PAGE_ROUTE.DASHBOARD_SUBMIT_GUARANTEE} element={<GuarantorSubmitInvitationPage />} />
								<Route path={PAGE_ROUTE.DASHBOARD_MORTGAGE_OFFER_LETTER} element={<MortgageOfferAndPaymentStepPage />} />
								<Route path={PAGE_ROUTE.VIEW_MORTGAGE_DEAL_FORM} element={<ViewMortgageDealForm />} />

								<Route path={PAGE_ROUTE.DASHBOARD_MORTGAGE_OFFER_LETTER_PREVIEW} element={<OfferLetterPreviewPage/>} />
								<Route path={PAGE_ROUTE.OFFER_PAYMENT_RESULT_PATH} element={<PaymentResultPage/>} />

								<Route path="*" element={<Navigate to={PAGE_ROUTE.NOT_FOUND} replace />} />
							</Routes>
						</BrowserRouter>
					</Suspense>
				</RootStoreProvider>
			</I18nextProvider>
		</div>
	);
};

export default PropCapApp;
